import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, Injector, Optional } from '@angular/core';
import { CustomError } from '@orthofi/ngx-error-handler/custom-error.service';
import { IErrorHandlerOptions, ILoggerService, INotificationService } from '@orthofi/ngx-error-handler/error-handler-interfaces.service';
import { ErrorHandlerService } from '@orthofi/ngx-error-handler/error-handler.service';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, filter, map, tap } from 'rxjs/operators';
import { IBaseEnvironmentConfig } from '../../../models/environment/IBaseEnvironmentConfig';
import { IAuthenticatedUser, IAuthenticatedUserRes } from '../../../models/user-interfaces';
import {
    ERROR_HANDLER_OPTIONS,
    LOGGER_SERVICE,
    LOGIN_URL,
    NOTIFICATION_SERVICE,
    SHOW_ERROR_DETAILS
} from '../../orthofi-keymaster-api/authenticated-user/tokens';

/** Credentials to be used when interacting with the OrthoFi Gatekeeper API */
const httpOptions = { withCredentials: true };

/**
 * An API service for retrieving information about the logged in visitor. This service also caches the information
 * so that information can be retrieved again without an additional HTTP request. If up to date information is required
 * in an application, ensure calls are made to `get` and not `retrieve`.
 */
@Injectable({ providedIn: 'root' })
export class OfiAuthenticatedUserAPIService {
    /** The URL the API is hosted at. No trailing slash. */
    private _apiRoot;

    /** A behaviorSubject containing the last retrieved value from the service's observable */
    user: BehaviorSubject<IAuthenticatedUser> = new BehaviorSubject(undefined);
    /** An observable of the practice list */
    user$ = this.user.asObservable();

    /** deprecated. Use `findHighestUserRole()` */
    getHighestUserRole = this.findHighestUserRole;

    private _errorHandler: ErrorHandlerService;

    // Had to modify the constructor to just pass in the tokens that are needed to instantiate the ErrorHandlerService
    // in the OrthoFi ngx-error-handler library. I tried to use the 'providers' to automatically inject the tokens
    // but could never get that to work post Angular 15 upgrade. Someone much better at Angular can maybe someday figure that out.
    // but for now, this works!
    constructor (
        @Optional() @Inject('ANGULAR_ENV') private _env: IBaseEnvironmentConfig,
        private _http: HttpClient,
        @Optional() @Inject(SHOW_ERROR_DETAILS) private showErrorDetails: boolean,
        @Optional() @Inject(LOGGER_SERVICE) private loggerService: ILoggerService,
        @Optional() @Inject(NOTIFICATION_SERVICE) private notificationService: INotificationService,
        @Optional() @Inject(LOGIN_URL) private loginUrl: string,
        @Optional() @Inject(ERROR_HANDLER_OPTIONS) private errorHandlerOptions: IErrorHandlerOptions
    ) {
        // Manually create ErrorHandlerService using tokens if they are provided
        if (
            showErrorDetails !== null &&
			loggerService !== null &&
			notificationService !== null &&
			loginUrl !== null &&
			errorHandlerOptions !== null
        ) {
            this._errorHandler = new ErrorHandlerService(showErrorDetails, loggerService, notificationService, loginUrl, errorHandlerOptions);
        } else {
            console.warn('Failed to inject all necessary dependencies for ErrorHandlerService');
        }

        // Don't want to make library consumers require this for everything, but @optional seems to be acting funky in a library.
        this._env = _env || ({ gatekeeperUrl: 'https://gatekeeper.orthofi.com' } as IBaseEnvironmentConfig);
        this._apiRoot = _env.gatekeeperUrl ? `${_env.gatekeeperUrl}/api/v1` : `${this._apiRoot}/api/v1`;
        // this.getUser().subscribe((authenticatedUser) => {
        //   this.user.next(authenticatedUser);
        //   return this.user;
        // });
    }

    /**
	 * Return a behaviorSubject's observable with the cached practice list. If we haven't obtained it from the server yet,
	 * call for the information. Note that if nothing is cached yet, undefined will be the first value emitted from the
	 * observable.
	 */
    resolveUser = (): Observable<IAuthenticatedUser> => {
        if (this.user.value === undefined) {
            this.getUser().subscribe();
        }
        return this.user$;
    };

    /**
	 * Return the behaviorSubject with the logged in user's details. If we haven't obtained those from the server yet, do
	 * so before returning--passing the newly loaded user data to sentry and Google Tag Manager along the way.
	 *
	 * @deprecated This.user is now an observable property. Either subscribe to it and call
	 * `getUser()` or call `resolveUser()` and use the returned reference to that observable.
	 */
    public getAuthenticatedUser = (): BehaviorSubject<IAuthenticatedUser> => {
        if (this.user.value === undefined) {
            this.getUser().subscribe((authenticatedUser) => {
                this.user.next(authenticatedUser);
            });
        }
        return this.user;
    };

    /**
	 * Make an HTTP call to get details about the visitor currently using the site, to unwrap details inside, and update
	 * the error logger with the user's information.
	 *
	 * Our HTTP endpoint returns the user information wrapped in an object named `User`, which this method abstracts away,
	 * returning only the child, not `{User: User: ...}}`
	 *
	 * @param updateContext Update the user in the error logger context with the user response. This is true by default
	 * and a value we generally want to keep up to date.
	 */
    public getUser = (updateErrorContext = true): Observable<IAuthenticatedUser> => {
        const url = `${this._apiRoot}/configuration/clientconfig/2`;
        return this._http.get<IAuthenticatedUserRes>(url, httpOptions).pipe(
            catchError((userError: any) => {
                if (userError.status !== 401) {
                    return throwError(
                        () =>
                            new CustomError('Sorry, we couldn\'t retrieve the user information. Please try again.', {
                                showSupportId: true,
                                errorObject: userError
                            })
                    );
                } else {
                    throw userError;
                }
            }),
            filter((authenticatedUser) => !!authenticatedUser.User),
            map((x) => x?.User),
            tap((authenticatedUser) => {
                this.user.next(authenticatedUser);
            }),
            tap((authenticatedUser: IAuthenticatedUser) => {
                if (updateErrorContext) {
                    // Update Sentry with any updated user.
                    if (this._errorHandler?.logger) {
                        this._errorHandler.logger.configureContext({
                            user: {
                                email: authenticatedUser.UserName || undefined,
                                username: authenticatedUser.UserName || undefined,
                                id: authenticatedUser.UserId || undefined
                            }
                        });
                    } else {
                        if (!this._errorHandler) {
                            console.warn('ErrorHandler is not initialized');
                        } else if (!this._errorHandler.logger) {
                            console.warn('Logger is not initialized');
                        }
                    }
                }
            })
        );
    };

    /**
	 * When we need to identify a user by a single role, this will attempt to return the role we're generally most
	 * interested in knowing about.
	 *
	 * @param roles a list of roles belonging to the user.
	 */
    findHighestUserRole (roles: Array<string>): string {
        const rolesByPriority = [
            'Administrator',
            'CSR',
            'Outsourcer',
            'Practice Administrator',
            'Practice Business Manager',
            'Doctor',
            'Practice Staff',
            'Guardian',
            'Staging Guardian',
            'Patient'
        ];
        if (!roles) {
            return 'N/A';
        }
        const matchedRole = rolesByPriority.reduce((current, prev, i, arr): string => {
            if (roles.indexOf(current) >= 0) {
                // If the shoe fits...
                arr.splice(i - 1); // We must lob off the rest of the array we're currently iterating on so we break early.
                return current;
            }
        });
        return matchedRole || roles[0] || undefined;
    }

    /**
	 * Check to see if the item is a given list. The default array checked against is a list of admin and practice staff roles,
	 * making this an easy way to check if a provided role is in the list of allowed admin or practice staff roles.
	 *
	 * @param role The User's role
	 * @param allowedRoles The list of roles the user must match one of. By default Admins, Staff Admins, and Doctors are included.
	 */
    isAllowed (
        role,
        allowedRoles = [ 'Administrator', 'CSR', 'Doctor', 'Practice Staff', 'Practice Administrator', 'Practice Business Manager' ]
    ): boolean {
        return new RegExp(allowedRoles.join('|')).test(role);
    }
}
